import { useState } from "react";
import { useLocation, useRoute, Link } from "wouter";
import { subscriptionAllowsAccess, useAuth } from "@/context/auth";
import { trackEvent } from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, ArrowRight, ArrowLeft, Info, Eye, EyeOff } from "lucide-react";
import { BUSINESS_TYPES, type BusinessType } from "@/lib/business-types";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");
const LOGO = `${import.meta.env.BASE_URL}logo.png`;

const BG_MAP: Record<string, string> = {
  supermarket:   `${import.meta.env.BASE_URL}bg-supermarket.png`,
  fashion:       `${import.meta.env.BASE_URL}bg-fashion.png`,
  ready_to_wear: `${import.meta.env.BASE_URL}bg-boutique.png`,
  food:          `${import.meta.env.BASE_URL}bg-food.png`,
  salon:         `${import.meta.env.BASE_URL}bg-salon.png`,
  tailor:        `${import.meta.env.BASE_URL}bg-tailor.png`,
  pharmacy:      `${import.meta.env.BASE_URL}bg-pharmacy.png`,
  gadget_repair: `${import.meta.env.BASE_URL}bg-gadget.png`,
  building:      `${import.meta.env.BASE_URL}bg-building.png`,
  laundry:       `${import.meta.env.BASE_URL}bg-laundry.png`,
  fabric_textile:`${import.meta.env.BASE_URL}bg-fabric.png`,
};

const TINT_MAP: Record<string, string> = {
  supermarket:   "from-emerald-950/80 to-slate-950/70",
  fashion:       "from-rose-950/80 to-slate-950/70",
  ready_to_wear: "from-pink-950/80 to-slate-950/70",
  food:          "from-orange-950/80 to-slate-950/70",
  salon:         "from-purple-950/80 to-slate-950/70",
  tailor:        "from-rose-950/80 to-slate-950/70",
  pharmacy:      "from-teal-950/80 to-slate-950/70",
  gadget_repair: "from-blue-950/80 to-slate-950/70",
  building:      "from-stone-950/80 to-slate-950/70",
  laundry:       "from-cyan-950/80 to-slate-950/70",
  fabric_textile:"from-purple-950/80 to-slate-950/70",
};

const RING_MAP: Record<string, string> = {
  supermarket:   "ring-emerald-500/40",
  fashion:       "ring-rose-500/40",
  ready_to_wear: "ring-pink-500/40",
  food:          "ring-orange-500/40",
  salon:         "ring-purple-500/40",
  tailor:        "ring-rose-500/40",
  pharmacy:      "ring-teal-500/40",
  gadget_repair: "ring-blue-500/40",
  building:      "ring-stone-500/40",
  laundry:       "ring-cyan-500/40",
  fabric_textile:"ring-purple-500/40",
};

const BTN_MAP: Record<string, string> = {
  supermarket:   "bg-emerald-600 hover:bg-emerald-700",
  fashion:       "bg-rose-600 hover:bg-rose-700",
  ready_to_wear: "bg-pink-600 hover:bg-pink-700",
  food:          "bg-orange-600 hover:bg-orange-700",
  salon:         "bg-purple-600 hover:bg-purple-700",
  tailor:        "bg-rose-600 hover:bg-rose-700",
  pharmacy:      "bg-teal-600 hover:bg-teal-700",
  gadget_repair: "bg-blue-600 hover:bg-blue-700",
  building:      "bg-stone-600 hover:bg-stone-700",
  laundry:       "bg-cyan-700 hover:bg-cyan-800",
  fabric_textile:"bg-purple-600 hover:bg-purple-700",
};

export default function BusinessLogin() {
  const [, params] = useRoute("/login/:type");
  const type = params?.type as BusinessType | undefined;
  const config = type && BUSINESS_TYPES[type] ? BUSINESS_TYPES[type] : null;

  const [, setLocation] = useLocation();
  const { refetch } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showDemo, setShowDemo] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${BASE}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: email.trim(), password }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || "Login failed"); return; }
      await refetch();
      trackEvent("login_succeeded", {
        login_type: "industry",
        industry: type ?? "unknown",
        access_active: subscriptionAllowsAccess(data.store),
      });
      setLocation(subscriptionAllowsAccess(data.store) ? "/" : "/subscribe");
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fillDemo = () => {
    if (config) {
      setEmail(config.demoEmail);
      setPassword(config.demoPassword);
    }
  };

  const bgImg  = type ? (BG_MAP[type]  ?? "") : "";
  const tint   = type ? (TINT_MAP[type]  ?? "from-slate-950/80 to-slate-950/70") : "from-slate-950/80 to-slate-950/70";
  const ring   = type ? (RING_MAP[type]  ?? "ring-white/20") : "ring-white/20";
  const btnCls = type ? (BTN_MAP[type]   ?? "bg-indigo-600 hover:bg-indigo-700") : "bg-indigo-600 hover:bg-indigo-700";

  return (
    <div className="relative min-h-screen flex items-center justify-center p-4 overflow-hidden">

      {/* ── Full-bleed background photo ── */}
      {bgImg ? (
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${bgImg})` }}
        />
      ) : (
        <div className="absolute inset-0 bg-slate-950" />
      )}

      {/* ── Gradient colour tint overlay ── */}
      <div className={`absolute inset-0 bg-gradient-to-br ${tint}`} />

      {/* ── Bottom vignette for extra contrast ── */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20" />

      {/* ── Login card (glassmorphism) ── */}
      <div className="relative z-10 w-full max-w-md">

        {/* Card shell */}
        <div className={`backdrop-blur-xl bg-white/10 border border-white/20 rounded-3xl shadow-2xl ring-1 ${ring} overflow-hidden`}>

          {/* Coloured top accent strip */}
          {config && (
            <div className={`h-1 w-full ${btnCls.split(" ")[0]}`} />
          )}

          <div className="p-8">

            {/* Logo + brand */}
            <div className="flex items-center justify-center gap-2.5 mb-6">
              <img src={LOGO} alt="RetailPulsePOS" className="w-8 h-8 rounded-lg object-cover" />
              <span className="text-lg font-bold text-white tracking-tight">RetailPulsePOS</span>
            </div>

            {/* Business identity */}
            {config ? (
              <div className="text-center mb-6">
                <div className="text-5xl mb-3 drop-shadow-lg">{config.emoji}</div>
                <h1 className="text-2xl font-bold text-white mb-1">{config.label}</h1>
                <p className="text-white/60 text-sm leading-relaxed">{config.tagline}</p>
              </div>
            ) : (
              <h1 className="text-2xl font-bold text-white text-center mb-6">Sign in to RetailPulsePOS</h1>
            )}

            {/* Demo banner */}
            {config && (
              <div className="mb-5">
                <button
                  onClick={() => setShowDemo(v => !v)}
                  className="w-full flex items-center gap-2 px-4 py-2.5 bg-white/10 border border-white/15 rounded-xl text-sm text-white/80 hover:bg-white/15 transition-colors"
                >
                  <Info className="w-3.5 h-3.5 text-white/50 flex-shrink-0" />
                  <span>Try the {config.shortLabel} demo account</span>
                  <span className="ml-auto text-white/40 text-xs">{showDemo ? "▲" : "▼"}</span>
                </button>
                {showDemo && (
                  <div className="mt-2 px-4 py-3 bg-black/30 border border-white/10 rounded-xl text-sm backdrop-blur-sm">
                    <div className="text-white/50 mb-2 text-xs uppercase tracking-wide">Demo credentials</div>
                    <div className="text-white font-mono text-xs mb-1">📧 {config.demoEmail}</div>
                    <div className="text-white font-mono text-xs mb-3">🔑 {config.demoPassword}</div>
                    <button
                      onClick={fillDemo}
                      className="w-full py-1.5 text-xs font-medium rounded-lg border border-white/20 text-white/80 hover:bg-white/10 transition-colors"
                    >
                      Fill demo credentials
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-1.5 block">
                  Email
                </label>
                <Input
                  type="email"
                  placeholder="you@yourstore.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/30 focus:border-white/40 focus:bg-white/15 h-11 rounded-xl"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-semibold text-white/70 uppercase tracking-wide">Password</label>
                  <Link href="/forgot-password" className="text-xs text-white/50 hover:text-white/80 transition-colors">
                    Forgot?
                  </Link>
                </div>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    className="bg-white/10 border-white/20 text-white placeholder:text-white/30 focus:border-white/40 focus:bg-white/15 h-11 rounded-xl pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70 transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {error && (
                <p className="text-sm text-red-300 bg-red-500/20 border border-red-500/30 px-3 py-2 rounded-xl">
                  {error}
                </p>
              )}

              <Button
                type="submit"
                className={`w-full h-11 gap-2 rounded-xl font-semibold text-white border-0 shadow-lg ${btnCls}`}
                disabled={loading}
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                Sign In to {config?.shortLabel || "RetailPulsePOS"}
              </Button>

              <p className="text-center text-sm text-white/50">
                No account?{" "}
                <Link href="/signup" className="text-white/80 font-semibold hover:text-white transition-colors">
                  Start free trial
                </Link>
              </p>
            </form>
          </div>
        </div>

        {/* Back link */}
        <div className="text-center mt-5">
          <Link
            href="/login"
            className="inline-flex items-center gap-1.5 text-sm text-white/50 hover:text-white/80 transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back to all business types
          </Link>
        </div>
      </div>
    </div>
  );
}
